import apiService from './apiService';

/**
 * Enhanced Profile Service with single API call optimization
 * FIXED: Eliminates multiple /user API calls that cause race conditions
 */
class ProfileService {
  constructor() {
    this.localServiceHistory = null;
    this._cache = new Map(); // key: userId, value: { promise, ts }
    this._CACHE_TTL = 30000; // 30 seconds
  }

  /**
   * Get complete profile data with a single API call.
   * Returns a cached Promise for the same userId within the 30s TTL window,
   * deduplicating concurrent mounts that each call getProfile/getServiceHistory/getClaims.
   */
  async getCompleteProfile(userId) {
    if (!userId) {
      return {
        profile: this.createDefaultProfile(userId),
        serviceHistory: { services: [], serviceEvents: [] },
        claims: []
      };
    }

    const cached = this._cache.get(userId);
    if (cached && (Date.now() - cached.ts) < this._CACHE_TTL) {
      return cached.promise;
    }

    const promise = this._fetchCompleteProfile(userId);
    this._cache.set(userId, { promise, ts: Date.now() });
    promise.catch(() => this._cache.delete(userId));
    return promise;
  }

  /**
   * Internal: performs the actual API call for complete profile data.
   * Called by getCompleteProfile when cache is cold or stale.
   */
  async _fetchCompleteProfile(userId) {
    try {
      const api = await apiService.getConfiguredApi();
      const response = await api.get('/user');

      if (response.data) {
        const profileData = this.formatProfileData(response.data);
        const serviceHistory = this.extractServiceHistory(response.data);
        const claims = this.extractClaims(response.data);

        return {
          profile: profileData,
          serviceHistory: serviceHistory,
          claims: claims
        };
      }

      console.log('ℹ️ No profile found, returning default structure');
      return {
        profile: this.createDefaultProfile(userId),
        serviceHistory: { services: [], serviceEvents: [] },
        claims: []
      };

    } catch (error) {
      // A genuine 404 means the profile does not exist yet (new user): it is
      // safe to return an empty structure. For ANY other failure (network,
      // 401 mid-refresh, 5xx, throttle) we must NOT return empty claims — the
      // read-modify-write claim writers (addClaim/addClaimsBatch/updateClaim)
      // merge into `existing.claims` and PUT the whole array, so an empty
      // result here would silently overwrite the veteran's real claims with a
      // wipe. Re-throw so those writers abort before the PUT.
      const status = error?.response?.status;
      if (status === 404) {
        console.log('ℹ️ No profile found (404), returning default structure');
        return {
          profile: this.createDefaultProfile(userId),
          serviceHistory: { services: [], serviceEvents: [] },
          claims: []
        };
      }
      console.error('❌ Error fetching complete profile:', error);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * LEGACY METHOD - Now calls getCompleteProfile and returns profile only
   */
  async getProfile(userId) {
    const completeProfile = await this.getCompleteProfile(userId);
    return completeProfile.profile;
  }

  /**
   * Extract service history from single API response
   * FIXED: No longer makes separate API call
   */
  extractServiceHistory(userData) {
    try {
      const serviceHistory = userData.serviceHistory;

      if (Array.isArray(serviceHistory)) {
        // Legacy format - convert to new format
        return {
          services: serviceHistory,
          serviceEvents: []
        };
      } else if (serviceHistory && (serviceHistory.services || serviceHistory.serviceEvents)) {
        // New format
        return {
          services: serviceHistory.services || [],
          serviceEvents: serviceHistory.serviceEvents || []
        };
      }

      return { services: [], serviceEvents: [] };
    } catch (error) {
      console.error('❌ Error extracting service history:', error);
      return { services: [], serviceEvents: [] };
    }
  }

  /**
   * LEGACY METHOD - Now extracts from cached data instead of making API call
   */
  async getServiceHistory(userId) {
    const completeProfile = await this.getCompleteProfile(userId);
    return completeProfile.serviceHistory;
  }

  /**
   * Extract claims from single API response
   * FIXED: No longer makes separate API call
   */
  extractClaims(userData) {
    try {
      if (userData.claims && Array.isArray(userData.claims)) {
        return userData.claims.map(claim => this.formatClaim(claim));
      }
      return [];
    } catch (error) {
      console.error('❌ Error extracting claims:', error);
      return [];
    }
  }

  /**
   * LEGACY METHOD - Now extracts from cached data instead of making API call
   */
  async getClaims(userId) {
    const completeProfile = await this.getCompleteProfile(userId);
    return completeProfile.claims;
  }

  /**
   * Update profile data in DynamoDB
   */
  async updateProfile(userId, updates) {
    try {
      this._cache.delete(userId);
      console.log('Updating profile');

      const api = await apiService.getConfiguredApi();
      const response = await api.put('/user', updates);

      if (response.data) {
        console.log('✅ Profile updated successfully in DynamoDB');
        return this.formatProfileData(response.data);
      }

      throw new Error('No data returned from profile update');

    } catch (error) {
      console.error('❌ Error updating profile:', error);
      throw new Error(`Failed to update profile: ${error.message}`);
    }
  }

  /**
   * Update service history (services and events) in DynamoDB with fallback
   */
  async updateServiceHistory(userId, serviceHistoryData) {
    try {
      this._cache.delete(userId);
      console.log('Updating service history');

      const api = await apiService.getConfiguredApi();
      const response = await api.put('/user', {
        serviceHistory: serviceHistoryData
      });

      if (response.data && response.data.serviceHistory) {
        console.log('✅ Service history updated successfully in DynamoDB');
        return response.data.serviceHistory;
      }

      console.log('✅ Service history update completed');
      return serviceHistoryData;

    } catch (error) {
      console.error('❌ Error updating service history:', error);
      console.log('📝 Service history stored locally (DynamoDB unavailable)');
      this.localServiceHistory = serviceHistoryData;
      return serviceHistoryData;
    }
  }

  /**
   * Add service record (legacy method - now updates full service history)
   */
  async addServiceRecord(userId, serviceRecord) {
    try {
      this._cache.delete(userId);
      console.log('Adding service record');

      const currentHistory = await this.getServiceHistory(userId);

      const timestamp = new Date().toISOString();
      const serviceId = serviceRecord.serviceId || `service_${Date.now()}`;

      const newServiceRecord = {
        serviceId,
        ...serviceRecord,
        createdAt: timestamp,
        updatedAt: timestamp
      };

      const updatedHistory = {
        services: [...(currentHistory.services || []), newServiceRecord],
        serviceEvents: currentHistory.serviceEvents || []
      };

      await this.updateServiceHistory(userId, updatedHistory);

      console.log('✅ Service record added successfully');
      return newServiceRecord;

    } catch (error) {
      console.error('❌ Error adding service record:', error);
      throw new Error(`Failed to add service record: ${error.message}`);
    }
  }

  /**
   * Add claim to DynamoDB
   */
  async addClaim(userId, claimData) {
    try {
      this._cache.delete(userId);
      const existing = await this.getCompleteProfile(userId);
      const currentClaims = existing.claims || [];
      const newClaimId = claimData.claimId || (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `claim-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      const newClaim = { ...claimData, claimId: newClaimId };
      const merged = [...currentClaims, newClaim];

      const api = await apiService.getConfiguredApi();
      const response = await api.put('/user', { claims: merged });

      if (response.data) {
        this._cache.delete(userId);
        return this.formatClaim(newClaim);
      }

      throw new Error('No data returned from claim addition');

    } catch (error) {
      console.error('❌ Error adding claim:', error);
      throw new Error(`Failed to add claim: ${error.message}`);
    }
  }

  async addClaimsBatch(userId, newClaims) {
    try {
      this._cache.delete(userId);
      const existing = await this.getCompleteProfile(userId);
      const currentClaims = existing.claims || [];

      const existingNames = new Set(
        currentClaims.map(c =>
          (c.conditionDetails?.primaryCondition || c.description || '').toLowerCase()
        )
      );

      const toAdd = newClaims.filter(
        c => !existingNames.has((c.conditionDetails?.primaryCondition || '').toLowerCase())
      );

      if (toAdd.length === 0) return { added: 0, claims: currentClaims.map(c => this.formatClaim(c)) };

      const withIds = toAdd.map(c => ({
        ...c,
        claimId: typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `claim-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      }));

      const merged = [...currentClaims, ...withIds];
      const api = await apiService.getConfiguredApi();
      const response = await api.put('/user', { claims: merged });

      if (response.data) {
        this._cache.delete(userId);
        return { added: withIds.length, claims: merged.map(c => this.formatClaim(c)) };
      }

      throw new Error('No data returned from batch claim addition');
    } catch (error) {
      throw new Error(`Failed to add claims batch: ${error.message}`);
    }
  }

  async updateClaim(userId, claimId, updates) {
    try {
      this._cache.delete(userId);
      const existing = await this.getCompleteProfile(userId);
      const currentClaims = existing.claims || [];

      let matched = false;
      const merged = currentClaims.map(c => {
        if (matched) return c;
        // Primary: match by claimId
        if (claimId && c.claimId && c.claimId === claimId) {
          matched = true;
          return { ...c, ...updates, claimId };
        }
        // Fallback: legacy claims without claimId — match by condition name
        if (!c.claimId && updates.conditionDetails?.primaryCondition &&
            (c.conditionDetails?.primaryCondition || '').toLowerCase() ===
            updates.conditionDetails.primaryCondition.toLowerCase()) {
          matched = true;
          return { ...c, ...updates, claimId };
        }
        return c;
      });
      if (!matched) {
        merged.push({ ...updates, claimId });
      }

      const api = await apiService.getConfiguredApi();
      const response = await api.put('/user', { claims: merged });

      if (response.data) {
        this._cache.delete(userId);
        const updated = merged.find(c => c.claimId === claimId);
        return this.formatClaim(updated);
      }

      throw new Error('No data returned from claim update');

    } catch (error) {
      console.error('❌ Error updating claim:', error);
      throw new Error(`Failed to update claim: ${error.message}`);
    }
  }

  /**
   * Delete a claim from DynamoDB by claimId (read-modify-write, mirroring
   * updateClaim).
   *
   * IMPORTANT: the getCompleteProfile call is deliberately NOT wrapped in a
   * try/catch. Its throw-on-error semantics (anything other than a genuine
   * 404) are the claim-wipe guard: if the read fails we must abort before the
   * PUT, otherwise we would PUT a filtered empty/stale claims array and wipe
   * the veteran's real claims. Do not weaken this.
   */
  async deleteClaim(userId, claimId) {
    this._cache.delete(userId);
    const existing = await this.getCompleteProfile(userId);
    const currentClaims = existing.claims || [];
    const remaining = currentClaims.filter(c => c.claimId !== claimId);

    // No-op: claimId not present in the array — nothing to delete, skip the PUT.
    if (remaining.length === currentClaims.length) {
      return { deleted: false, claims: currentClaims.map(c => this.formatClaim(c)) };
    }

    try {
      const api = await apiService.getConfiguredApi();
      const response = await api.put('/user', { claims: remaining });

      if (response.data) {
        this._cache.delete(userId);
        return { deleted: true, claims: remaining.map(c => this.formatClaim(c)) };
      }

      throw new Error('No data returned from claim deletion');

    } catch (error) {
      console.error('❌ Error deleting claim:', error);
      throw new Error(`Failed to delete claim: ${error.message}`);
    }
  }

  /**
   * Create default profile structure
   */
  createDefaultProfile(userId) {
    return {
      userId: userId || '',
      fullName: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      userType: 'veteran',
      street: '',
      city: '',
      state: '',
      postcode: '',
      country: 'Australia',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      serviceNumber: '',
      dvaNumber: '',
      veteranCardType: '',
      totalServiceYears: 0,
      totalImpairmentRating: 0,
      personalInfo: {
        fullName: '',
        dateOfBirth: '',
        userType: 'veteran',
        address: {
          street: '',
          city: '',
          state: '',
          postcode: '',
          country: 'Australia'
        },
        contactInfo: {
          phone: '',
          email: '',
          emergencyContact: {
            name: '',
            relationship: '',
            phone: ''
          }
        },
        veteranDetails: {
          serviceNumber: '',
          dvaNumber: '',
          veteranCardType: '',
          totalServiceYears: 0,
          totalImpairmentRating: 0
        }
      }
    };
  }

  /**
   * Format profile data from DynamoDB structure
   */
  formatProfileData(item) {
    const personalInfo = item.personalInfo || {};
    const contactInfo = personalInfo.contactInfo || {};
    const address = personalInfo.address || {};
    const veteranDetails = personalInfo.veteranDetails || {};
    const emergencyContact = contactInfo.emergencyContact || {};

    return {
      userId: item.userId || '',
      fullName: item.fullName || personalInfo.fullName || '',
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      email: item.email || contactInfo.email || '',
      phoneNumber: item.phoneNumber || contactInfo.phone || '',
      dateOfBirth: item.dateOfBirth || personalInfo.dateOfBirth || '',
      userType: item.userType || personalInfo.userType || 'veteran',
      street: item.street || address.street || '',
      city: item.city || address.city || '',
      state: item.state || address.state || '',
      postcode: item.postcode || address.postcode || '',
      country: item.country || address.country || 'Australia',
      emergencyContactName: item.emergencyContactName || emergencyContact.name || '',
      emergencyContactRelationship: item.emergencyContactRelationship || emergencyContact.relationship || '',
      emergencyContactPhone: item.emergencyContactPhone || emergencyContact.phone || '',
      serviceNumber: item.serviceNumber || veteranDetails.serviceNumber || '',
      dvaNumber: item.dvaNumber || veteranDetails.dvaNumber || '',
      veteranCardType: item.veteranCardType || veteranDetails.veteranCardType || '',
      totalServiceYears: item.totalServiceYears || veteranDetails.totalServiceYears || 0,
      totalImpairmentRating: item.totalImpairmentRating || veteranDetails.totalImpairmentRating || 0,
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || '',
      personalInfo: personalInfo,
      // Preserve consent + onboarding fields for AIConsentGate and consent-version checks.
      // Backend stores these at the top level AND inside personalInfo.consentSettings.
      consentSettings: item.consentSettings || personalInfo.consentSettings || {},
      acceptedConsentVersion: item.acceptedConsentVersion || null,
      currentConsentVersion: item.currentConsentVersion || null,
      onboardingComplete: item.onboardingComplete === true,
      onboardingSkipped: item.onboardingSkipped === true
    };
  }

  /**
   * Format service record from DynamoDB structure
   */
  formatServiceRecord(item) {
    return {
      serviceId: item.serviceId || '',
      branch: item.branch || '',
      startDate: item.startDate || '',
      endDate: item.endDate || '',
      rank: item.rank || '',
      deployments: item.deployments || [],
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || ''
    };
  }

  /**
   * Format claim from DynamoDB structure
   */
  formatClaim(item) {
    // Preserve all backend fields — claims carry rich context
    // (conditionDetails, onsetYear, narrative, evidenceChecklist, etc.) that downstream
    // consumers (ConditionsSection, ClaimsDashboard) rely on for rendering.
    return {
      ...item,
      claimId: item.claimId || '',
      claimType: item.claimType || '',
      status: item.status || 'draft',
      submittedDate: item.submittedDate || '',
      documents: item.documents || [],
      description: item.description || '',
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || ''
    };
  }
}

// Export singleton instance
const profileService = new ProfileService();
export default profileService;
